/**
 * Générateur — Automatisme : produits en lien avec les tables (0,6 × 7)
 * Attendu ID : 1042 | Niveau : 5ème | Domaine : Opérations
 * Rubrique : AUTOMATISME (programme 2025) — deux composantes : récupérer le
 * fait de table (déclaratif) PUIS placer virgule/zéros (procédural).
 * Labels (cartes exerciseur) :
 *   - Produits habillés            (saisie à trous + QCM diagnostique)
 *   - Défi : décimal × décimal     (saisie, typé hors cœur)
 *
 * ── Plan de cartes validé (2026-08-31) ─────────────────────────────────────
 * Item = fait de table (b, c ∈ {2…9}) habillé par des puissances de dix :
 * facteurs b·10^e1 et c·10^e2. BORNES : au plus UN habillage négatif en cœur
 * (décimal × décimal = défi seul, e1 = e2 = −1), résultat à ≤ 2 décimales
 * (e1+e2 ≥ −2) et ≤ 4 chiffres ; jamais de mantisse 1 ni 10 ; pas de
 * relatifs, pas de posé, pas de 3 décimales, tables 11-12 exclues (l'item
 * source 0,12 × 3 est HORS cadrage, décision prof). STRICTEMENT l'attendu
 * « savoir calculer » : pas de format ordre de grandeur (objectif 6e voisin,
 * retiré à la revue de plan).
 *
 * INVARIANTS :
 *   - ZÉRO FLOTTANT : 0,6 × 7 vaut 4.199999… en IEEE : tous les calculs
 *     sont ENTIERS (mantisses, décalages), la virgule est posée par
 *     manipulation de chaîne (fmtDecale), et chaque égalité émise est
 *     RE-VÉRIFIÉE en centièmes entiers (throw sinon) ;
 *   - couverture PAR SÉRIE de trous : un « qui rapetisse », un « mixte »
 *     (décalages opposés, dont le précieux net nul : 0,6 × 70 = 42), un
 *     « ZÉRO FINAL » (0,5 × 4 = 2 : le tueur de la règle « je compte les
 *     chiffres après la virgule »), un « dizaines × entier » (40 × 8, le
 *     pont CM) ; ordre des facteurs alterné (0,6 × 7 / 7 × 0,6) ;
 *   - FAIT SACRIFIÉ aux fiches d'aide : (6, 7) dans les deux sens : toutes
 *     ses déclinaisons (0,6 × 7 ; 60 × 0,07 ; 60 × 7 ; 0,6 × 0,7) sont
 *     réservées aux coups de pouce et ne sortent JAMAIS en tirage
 *     (invariant protégé par cdp1042.self.test.js) ; le zéro final n'est
 *     PAS sacrifié (pool rare : paires avec 5) : il vit en explication
 *     post-réponse spécifique à l'item ;
 *   - QCM : distracteurs par famille SELON l'habillage de l'item (décimal :
 *     virgule_comptage / virgule_oubliee / fait_voisin ; entier : zero_oublie
 *     / zero_ajoute / fait_voisin ; mixte : multiplier_agrandit /
 *     virgule_comptage / fait_voisin), chacun re-vérifié ≠ bonne ;
 *   - explications contenu.explications SPÉCIFIQUES à l'item (la
 *     verbalisation de la source : « 6 × 7 = 42, donc 42 dixièmes = 4,2 »).
 *
 * ASSUMÉ à la revue (2026-08-31, mesures 5000, décisions prof) :
 *   - deux lignes d'une série de trous peuvent partager le même RÉSULTAT
 *     (0,5 % : faits différents, ex. 0,2 × 9 et 3 × 0,6 → 1,8) : aucune
 *     fuite (l'élève ne le découvre qu'en calculant les deux) ;
 *   - au QCM, la bonne réponse n'est jamais strictement la plus longue des
 *     options (les distracteurs par décalage ajoutent des caractères par
 *     nature) : méta-règle invisible en alternance d'exerciseurs, et la
 *     stratégie « plus courte » perd face au hasard (15 % < 25 %).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 1042;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_PRODUITS = 'Produits habillés';
const L_DEFI = 'Défi : décimal × décimal';

// ─── Le domaine ────────────────────────────────────────────────────────────

/** Le fait sacrifié aux fiches d'aide (les deux sens). */
export const FAIT_SACRIFIE = [6, 7];
const estSacrifie = (b, c) => (b === FAIT_SACRIFIE[0] && c === FAIT_SACRIFIE[1])
    || (b === FAIT_SACRIFIE[1] && c === FAIT_SACRIFIE[0]);

/**
 * Écriture décimale EXACTE de n × 10^k (n entier ≥ 1, k ∈ [−4, 4]) par
 * manipulation de chaîne : aucun flottant. Forme canonique : pas de zéros
 * décimaux finaux, virgule française.
 */
const fmtDecale = (n, k) => {
    let s = String(n);
    if (k >= 0) return s + '0'.repeat(k);
    const d = -k;
    s = s.padStart(d + 1, '0');
    let ent = s.slice(0, s.length - d);
    let dec = s.slice(s.length - d).replace(/0+$/, '');
    return dec === '' ? ent : `${ent},${dec}`;
};

/** Valeur en dix-millièmes (entier exact) d'une écriture « 4,2 » / « 320 ». */
const dixMilliemes = (str) => {
    const [ent, dec = ''] = String(str).split(',');
    const d4 = (dec + '0000').slice(0, 4);
    return parseInt(ent, 10) * 10000 + parseInt(d4, 10);
};

/**
 * AUTO-VÉRIFICATION d'une égalité émise, par une AUTRE voie que la
 * construction : les trois écritures sont re-parsées et le produit recomposé
 * en entiers (dix-millièmes) : throw si contradiction.
 */
const verifierEgalite = (f1, f2, resultat) => {
    const gauche = dixMilliemes(f1) * dixMilliemes(f2); // en 10^-8
    const droite = dixMilliemes(resultat) * 10000;      // en 10^-8
    if (gauche !== droite) {
        throw new Error(`gen_1042 : égalité fausse ${f1} × ${f2} = ${resultat}`);
    }
};

/** Virgule française en KaTeX : 0{,}6 (pas d'espace parasite). */
const enTex = (s) => String(s).replace(',', '{,}');

/**
 * Fabrique un item : fait (b, c) + habillages (e1, e2). Retourne les
 * écritures exactes et les métadonnées. Vérifie bornes et sacrifice.
 */
const fabriquerItem = (b, c, e1, e2) => {
    if (estSacrifie(b, c)) throw new Error('gen_1042 : fait sacrifié tiré');
    const net = e1 + e2;
    if (net < -2) throw new Error(`gen_1042 : ${net} décimales (max 2)`);
    const f1 = fmtDecale(b, e1);
    const f2 = fmtDecale(c, e2);
    const resultat = fmtDecale(b * c, net);
    if (resultat.replace(',', '').length > 5) throw new Error(`gen_1042 : résultat trop long ${resultat}`);
    verifierEgalite(f1, f2, resultat);
    return { b, c, e1, e2, net, f1, f2, resultat };
};

/** Un fait de table hors sacrifice, hors mantisse 1/10. */
const tirerFait = (filtre = null) => {
    for (let essai = 0; essai < 80; essai++) {
        const b = randInt(2, 9);
        const c = randInt(2, 9);
        if (estSacrifie(b, c)) continue;
        if (filtre && !filtre(b, c)) continue;
        return [b, c];
    }
    return [4, 5]; // repli sûr : jamais sacrifié ET compatible zéro final (20)
};

/** Verbalisation de la source, spécifique à l'item : l'explication d'après. */
const UNITES = { 0: '', '-1': ' dixièmes', '-2': ' centièmes' };
const expliquerItem = (it) => {
    const fait = `${it.b} × ${it.c} = ${it.b * it.c}`;
    if (it.net === 0 && (it.e1 !== 0 || it.e2 !== 0)) {
        return `${fait} : les décalages se compensent, donc ${it.f1} × ${it.f2} = ${it.resultat}.`;
    }
    if (it.net > 0) {
        return `${fait}, donc ${it.f1} × ${it.f2} = ${it.resultat} (on décale ${it.net === 1 ? 'd\'un zéro' : `de ${it.net} zéros`}).`;
    }
    if (it.net === 0) return `${fait}.`;
    return `${fait}, donc ${it.f1} × ${it.f2} = ${it.b * it.c}${UNITES[it.net]} = ${it.resultat}.`;
};

// ─── Carte 1, format A : la série à trous (le cœur) ───────────────────────

/** Les quatre catégories obligatoires d'une série (couverture prof). */
const tirerCategorie = (categorie) => {
    if (categorie === 'rapetisse') {
        // UN habillage négatif, l'autre nul : 0,6 × 7 ; 5 × 0,07.
        const [b, c] = tirerFait();
        const e = randomChoice([-1, -2]);
        return randomChoice([true, false]) ? fabriquerItem(b, c, e, 0) : fabriquerItem(b, c, 0, e);
    }
    if (categorie === 'mixte') {
        // Décalages opposés : 40 × 0,03 (net −1) ou 0,6 × 70 (net 0 : le
        // résultat entier depuis un facteur décimal, aussi précieux).
        const [b, c] = tirerFait();
        const eHaut = randomChoice([1, 2]);
        const eBas = randomChoice([-1, -2]);
        return randomChoice([true, false]) ? fabriquerItem(b, c, eHaut, eBas) : fabriquerItem(b, c, eBas, eHaut);
    }
    if (categorie === 'zero_final') {
        // b × c multiple de 10 (paires avec 5) et net < 0 : le zéro tombe,
        // la règle « je compte les chiffres après la virgule » meurt.
        const [b, c] = tirerFait((x, y) => (x * y) % 10 === 0);
        const e = randomChoice([-1, -2]);
        return randomChoice([true, false]) ? fabriquerItem(b, c, e, 0) : fabriquerItem(b, c, 0, e);
    }
    // 'dizaines' : tout entier, le pont CM : 40 × 8 = 320.
    const [b, c] = tirerFait();
    const e = randomChoice([1, 2]);
    return randomChoice([true, false]) ? fabriquerItem(b, c, e, 0) : fabriquerItem(b, c, 0, e);
};

const genProduitsTrous = () => {
    // 4 lignes = les 4 catégories, faits tous distincts, ordre mélangé.
    for (let essai = 0; essai < 60; essai++) {
        const items = ['rapetisse', 'mixte', 'zero_final', 'dizaines'].map(tirerCategorie);
        const faits = new Set(items.map((it) => [it.b, it.c].sort().join('x')));
        if (faits.size < 4) continue;
        const melange = shuffle(items);
        const phrase = melange.map((it) => `$${enTex(it.f1)} \\times ${enTex(it.f2)} = {{${it.resultat}}}$`).join('\n');
        const payload = makeTexteTrou(
            phrase,
            '<p><b>Calcule chaque produit.</b></p>',
            { ...opts(L_PRODUITS), mode: 'saisie' },
        );
        payload.contenu = { ...payload.contenu, explications: melange.map(expliquerItem) };
        payload._meta.params = { items: melange };
        return payload;
    }
    // Repli déterministe : une série sûre, catégories couvertes.
    const items = [
        fabriquerItem(8, 9, -1, 0),  // 0,8 × 9 = 7,2
        fabriquerItem(4, 3, 1, -2),  // 40 × 0,03 = 1,2
        fabriquerItem(5, 4, -1, 0),  // 0,5 × 4 = 2
        fabriquerItem(3, 7, 2, 0),   // 300 × 7 = 2100
    ];
    const payload = makeTexteTrou(
        items.map((it) => `$${enTex(it.f1)} \\times ${enTex(it.f2)} = {{${it.resultat}}}$`).join('\n'),
        '<p><b>Calcule chaque produit.</b></p>',
        { ...opts(L_PRODUITS), mode: 'saisie' },
    );
    payload.contenu = { ...payload.contenu, explications: items.map(expliquerItem) };
    payload._meta.params = { items };
    return payload;
};

// ─── Carte 1, format B : QCM diagnostique ─────────────────────────────────

/**
 * Distracteurs par famille SELON l'habillage : chacun une erreur nommée du
 * tableau prof (déclaratif vs procédural), re-vérifié ≠ bonne réponse.
 * fait_voisin : b × (c ± 1) HABILLÉ PAREIL : la table fausse, la virgule
 * juste : c'est lui qui sépare les deux composantes.
 */
const distracteursPour = (it) => {
    const delta = it.c >= 9 ? -1 : it.c <= 2 ? 1 : randomChoice([-1, 1]);
    const voisin = { texte: fmtDecale(it.b * (it.c + delta), it.net), code: 'fait_voisin' };
    const liste = [voisin];
    const toutEntier = it.e1 >= 0 && it.e2 >= 0;
    const mixte = (it.e1 > 0 && it.e2 < 0) || (it.e1 < 0 && it.e2 > 0);
    if (mixte) {
        liste.push({ texte: fmtDecale(it.b * it.c, it.net + 1), code: 'multiplier_agrandit' });
        liste.push({ texte: fmtDecale(it.b * it.c, it.net - 1), code: 'virgule_comptage' });
    } else if (toutEntier) {
        liste.push({ texte: fmtDecale(it.b * it.c, it.net - 1), code: 'zero_oublie' });
        liste.push({ texte: fmtDecale(it.b * it.c, it.net + 1), code: 'zero_ajoute' });
    } else {
        liste.push({ texte: fmtDecale(it.b * it.c, it.net - 1), code: 'virgule_comptage' });
        liste.push({ texte: fmtDecale(it.b * it.c, 0), code: 'virgule_oubliee' });
    }
    return liste;
};

const genHabillageQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const categorie = randomChoice(['rapetisse', 'mixte', 'zero_final', 'dizaines']);
        const it = tirerCategorie(categorie);
        const faux = distracteursPour(it);
        const textes = [it.resultat, ...faux.map((x) => x.texte)];
        // Tous distincts et tous ≠ bonne (le zéro final peut faire coïncider
        // un décalage avec la forme canonique : 0,30 → 0,3 ≠ 3 : on re-tire
        // si collision).
        if (new Set(textes).size !== 4) continue;
        faux.forEach((x) => {
            if (dixMilliemes(x.texte) === dixMilliemes(it.resultat)) {
                throw new Error(`gen_1042 : distracteur ${x.code} égal à la bonne réponse`);
            }
        });
        const payload = makeQcm(
            textes,
            it.resultat,
            `<p><b>Combien vaut ${tex(`${enTex(it.f1)} \\times ${enTex(it.f2)}`)} ?</b></p>`,
            opts(L_PRODUITS),
            false,
        );
        payload.contenu = { ...payload.contenu, explications: { item: expliquerItem(it) } };
        payload._meta.params = { ...it, categorie, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli déterministe : un item sûr dont les 4 écritures sont distinctes.
    const it = fabriquerItem(8, 9, -1, 0); // 0,8 × 9 = 7,2
    const faux = distracteursPour(it);
    const payload = makeQcm(
        [it.resultat, ...faux.map((x) => x.texte)],
        it.resultat,
        `<p><b>Combien vaut ${tex(`${enTex(it.f1)} \\times ${enTex(it.f2)}`)} ?</b></p>`,
        opts(L_PRODUITS),
        false,
    );
    payload.contenu = { ...payload.contenu, explications: { item: expliquerItem(it) } };
    payload._meta.params = { ...it, categorie: 'rapetisse', distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 1, format C : saisie directe (une question, mêmes catégories) ──

const genProduitSaisie = () => {
    const categorie = randomChoice(['rapetisse', 'mixte', 'zero_final', 'dizaines']);
    const it = tirerCategorie(categorie);
    const payload = makeSaisie(
        it.resultat,
        `<p><b>Calcule : ${tex(`${enTex(it.f1)} \\times ${enTex(it.f2)}`)}</b></p>`,
        opts(L_PRODUITS),
    );
    payload.contenu = { ...payload.contenu, explications: { item: expliquerItem(it) } };
    payload._meta.params = { ...it, categorie };
    return payload;
};

// ─── Carte 2 : le défi décimal × décimal ──────────────────────────────────

const genDefiSaisie = () => {
    // Strictement 0,b × 0,c (e1 = e2 = −1) : net −2, jamais 3 décimales.
    const [b, c] = tirerFait();
    const it = fabriquerItem(b, c, -1, -1);
    const payload = makeSaisie(
        it.resultat,
        `<p><b>Calcule : ${tex(`${enTex(it.f1)} \\times ${enTex(it.f2)}`)}</b></p>`,
        opts(L_DEFI),
    );
    payload.contenu = { ...payload.contenu, explications: { item: expliquerItem(it) } };
    payload._meta.params = { ...it };
    return payload;
};

// ─── Contrat distracteurs (référence de revue, format QCM) ────────────────

/**
 * Chaque erreur est une MISCONCEPTION NOMMÉE d'élève, adossée au tableau de
 * remédiation du cadrage (déclaratif vs procédural). Arbitrage prof
 * 2026-08-31.
 */
export const REGLES_DISTRACTEURS_QCM = {
    habillage_qcm: {
        support: 'le produit habillé de l\'item, 4 écritures : UNE seule vraie (re-vérifiée en entiers)',
        fixes: {
            fait_voisin: 'b × (c ± 1) habillé pareil : la TABLE est fausse, la virgule juste : diagnostic déclaratif',
            virgule_comptage: 'un cran de virgule en trop (0,42 pour 0,6 × 7) : la règle « je compte les chiffres »',
            virgule_oubliee: 'le fait brut sans virgule (42) : erreur d\'attention',
            multiplier_agrandit: 'un cran au-dessus (12 pour 40 × 0,03) : le faux automatisme « multiplier agrandit »',
            zero_oublie: 'un zéro de moins (32 pour 40 × 8) : procédural entier',
            zero_ajoute: 'un zéro de trop (3 200 pour 40 × 8) : procédural entier',
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    produits_texte_trou_saisie: genProduitsTrous,
    habillage_qcm: genHabillageQcm,
    produit_saisie: genProduitSaisie,
    defi_decimal_saisie: genDefiSaisie,
};

export const CATEGORIES = [
    { id: 'produits', label: L_PRODUITS, formatsList: [
        { key: 'produits_texte_trou_saisie', label: 'Saisie trous' },
        { key: 'habillage_qcm', label: 'QCM' },
        { key: 'produit_saisie', label: 'Saisie' },
    ] },
    { id: 'defi', label: L_DEFI, formatsList: [
        { key: 'defi_decimal_saisie', label: 'Saisie' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
